import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import {
  FeaturesConfigurationFeatureTypesForm,
  FeatureTypesConfigFormValues,
} from 'src/screens/administration/configuration-management/features-configuration/feature-types/edit/FeaturesConfigurationFeatureTypesForm'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'
import { OperationResult, useClient } from 'urql'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import {
  Feature_Config_Set_Input,
  Feature_Group_Config_Set_Input,
  Feature_Type_Config_Set_Input,
  Mutation_Root,
  Query_Root,
} from 'src/@types/graphql'
import {
  queryFeatureTypeConfigById,
  updateFeatureTypeConfigAndRelatedFeatureGroupsByTypeId,
  updateFeatureTypeConfigById,
} from '../featureTypesConfigQueries'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { resolveFeaturesConfigRoot } from 'src/routing/routing-utils'

type OriginType = 'SAVE' | 'SAVE_AND_BACK'

type UpdateFeatureTypeConfigResponse = OperationResult<{
  update_feature_type_config: Mutation_Root['update_feature_type_config']
  update_feature_group_config?: Mutation_Root['update_feature_group_config']
  update_feature_config?: Mutation_Root['update_feature_config']
}>

const submitFeatureTypeConfigForm = async () => {
  document.getElementById('feature-types-config-form')?.dispatchEvent(
    new Event('submit', {
      cancelable: true,
      bubbles: true,
    }),
  )
}

export const FeaturesConfigurationFeatureTypesEditPage = () => {
  const { featureTypeId } = useParams()
  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()
  const { getMessage } = useMessageSource()
  const { state } = useLocation()

  const [initialValues, setInitialValues] = useState<FeatureTypesConfigFormValues | null>(null)
  const originRef = useRef<OriginType>('SAVE')

  const onBackHandler = useCallback(() => {
    const processTypeFromState = state?.processType

    if (processTypeFromState !== null && processTypeFromState !== undefined) {
      navigate(resolveFeaturesConfigRoot(processTypeFromState))
    } else {
      navigate(ROUTES.FeaturesConfigurationIndex.nested.FeaturesConfigurationFeatureTypes.params({}))
    }
  }, [navigate, state])

  const onSave = async () => {
    originRef.current = 'SAVE'
    submitFeatureTypeConfigForm()
  }

  const onSaveAndBack = async () => {
    originRef.current = 'SAVE_AND_BACK'
    submitFeatureTypeConfigForm()
  }

  const fetchFeatureTypeToEdit = useCallback(async () => {
    if (!featureTypeId) {
      throw new Error()
    }

    const { data, error } = await urqlClient
      .query<{ feature_type_config: Query_Root['feature_type_config'] }>(queryFeatureTypeConfigById, {
        featureTypeId,
      })
      .toPromise()

    if (error || !data?.feature_type_config[0]) {
      throw new Error('Resource not found')
    }

    const fetchedFeatureTypeConfig = data.feature_type_config[0]
    const editingFeatureTypeConfig: FeatureTypesConfigFormValues = {
      enName: fetchedFeatureTypeConfig.names['en'],
      deName: fetchedFeatureTypeConfig.names['de'],
      frName: fetchedFeatureTypeConfig.names['fr'],
      itName: fetchedFeatureTypeConfig.names['it'],
      enDescription: fetchedFeatureTypeConfig.description['en'],
      deDescription: fetchedFeatureTypeConfig.description['de'],
      frDescription: fetchedFeatureTypeConfig.description['fr'],
      itDescription: fetchedFeatureTypeConfig.description['it'],
      status: fetchedFeatureTypeConfig.active ? CONFIGURATION_STATUS.ACTIVE : CONFIGURATION_STATUS.INACTIVE,
      hasKapFactsheetValidation: fetchedFeatureTypeConfig.has_kap_factsheet_validation,
      helpAndInstructionsKey: fetchedFeatureTypeConfig.help_and_instruction_key,
    }

    return { editingFeatureTypeConfig }
  }, [featureTypeId, urqlClient])

  const updateFeatureTypeConfigToActive = useCallback(
    async (featureTypeId: number, featureTypeConfigSetInput: Feature_Type_Config_Set_Input) => {
      return await urqlClient
        .mutation<{
          update_feature_type_config: Mutation_Root['update_feature_type_config']
        }>(updateFeatureTypeConfigById, {
          featureTypeId,
          featureTypeConfigSetInput,
        })
        .toPromise()
    },
    [urqlClient],
  )

  const updateFeatureTypeConfigToInactive = useCallback(
    async (featureTypeId: number, featureTypeConfigSetInput: Feature_Type_Config_Set_Input) => {
      const featureGroupConfigSetInput: Feature_Group_Config_Set_Input = {
        active: false,
      }
      const featureConfigSetInput: Feature_Config_Set_Input = {
        active: false,
      }

      return await urqlClient
        .mutation<{
          update_feature_type_config: Mutation_Root['update_feature_type_config']
          update_feature_group_config: Mutation_Root['update_feature_group_config']
          update_feature_config: Mutation_Root['update_feature_config']
        }>(updateFeatureTypeConfigAndRelatedFeatureGroupsByTypeId, {
          featureTypeId,
          featureTypeConfigSetInput,
          featureGroupConfigSetInput,
          featureConfigSetInput,
        })
        .toPromise()
    },
    [urqlClient],
  )

  const handleSubmitLocal = useCallback(
    async (values: FeatureTypesConfigFormValues) => {
      if (!featureTypeId) {
        notificationService.operationFailed()
        return
      }

      const isActiveSelected = values.status === CONFIGURATION_STATUS.ACTIVE
      const featureTypeConfigSetInput: Feature_Type_Config_Set_Input = {
        names: { de: values.deName, en: values.enName, fr: values.frName, it: values.itName },
        description: {
          de: values.deDescription,
          en: values.enDescription,
          fr: values.frDescription,
          it: values.itDescription,
        },
        active: isActiveSelected,
        has_kap_factsheet_validation: values.hasKapFactsheetValidation,
      }

      let updateFeatureTypeConfigResponse: UpdateFeatureTypeConfigResponse
      const featureTypeIdParsed = parseInt(featureTypeId as string)
      if (isActiveSelected) {
        updateFeatureTypeConfigResponse = await updateFeatureTypeConfigToActive(
          featureTypeIdParsed,
          featureTypeConfigSetInput,
        )
      } else {
        updateFeatureTypeConfigResponse = await updateFeatureTypeConfigToInactive(
          featureTypeIdParsed,
          featureTypeConfigSetInput,
        )
      }

      const { data, error } = updateFeatureTypeConfigResponse
      if (!error && data?.update_feature_type_config?.affected_rows === 1) {
        notificationService.changesSaved()
        setInitialValues(values)
        if (originRef.current === 'SAVE_AND_BACK') {
          onBackHandler()
        }
      } else {
        notificationService.operationFailed()
      }
    },
    [
      featureTypeId,
      notificationService,
      onBackHandler,
      updateFeatureTypeConfigToActive,
      updateFeatureTypeConfigToInactive,
    ],
  )

  useEffect(() => {
    fetchFeatureTypeToEdit()
      .then((res) => {
        setInitialValues(res.editingFeatureTypeConfig)
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
  }, [fetchFeatureTypeToEdit, notificationService])

  return (
    <ScreenLayout
      title={getMessage('label.configuration.features.type.edit')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSaveAndBack} />
          <SaveButton origin="header" onClick={onSave} />
        </>
      }
    >
      <PageLayout>
        <>
          {initialValues && (
            <FeaturesConfigurationFeatureTypesForm
              mode="EDIT"
              onSubmit={handleSubmitLocal}
              initialValues={initialValues}
            />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
